/**
 * The property's Map tab — `docs/property-maps-v2.md` §5.1.
 *
 * One component, five states, in the order §5.1 fixes: **no location** first
 * because there is nowhere to draw, then a measure session if the URL asked
 * for one, then the editor, then the Empty screen. The flag is not a sixth
 * state — it decides whether what you get is editable.
 *
 * **It is the same component on a phone**, choosing cards instead of a canvas
 * (§6.1): the data, the states and the words are shared and only the layout
 * differs, which is the rule the whole tab row follows.
 *
 * PR 2 is the read half. **Create a map** and **Start measuring** are wired to
 * what exists — the first calls `POST …/maps`, the second sets `?measure=1` —
 * and the editor draws; the drawing tools, the dialogs and the measure session
 * arrive in PRs 3 and 4.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { EmptyState } from '../components/primitives.js'
import { usePhone } from '../lib/use-narrow.js'
import { imagerySession } from '../lib/map-provider.js'
import { request } from '../lib/api.js'
import {
  createMap, oneMap, rememberLastMap, type MapLayer, type OneMapPayload,
} from './api.js'
import { usePropertyMaps } from './use-property-maps.js'
import { MapEmptyScreen } from './states/empty.js'
import { NoMapLocation } from './states/no-location.js'
import { MapEditor } from './editor/map-editor.js'
import { MapSwitcher } from './dialogs/switcher.js'
import { NewMapDialog } from './dialogs/new-map.js'
import { PhoneMapTab } from './phone/phone-map-tab.js'

export function PropertyMapTab({
  propertyId, propertyName, address, propertyVersion, canEdit,
  onOpenMeasurements, onPropertyChanged,
}: {
  propertyId: string
  propertyName: string
  address: string
  /** For the optimistic-concurrency check when a dropped pin is saved. */
  propertyVersion: number
  canEdit: boolean
  /** Overview › About › Measured, which is where the saved numbers live. */
  onOpenMeasurements: () => void
  /** A pin was saved, so the property record has to be re-read. */
  onPropertyChanged: () => void
}) {
  const phone = usePhone()
  const [params, setParams] = useSearchParams()
  const measuring = params.get('measure') === '1'
  /* `?panel=measurements` is how the Overview card's `3 parts · Map` link
     lands on the right tab of the right panel (§10). */
  const panel = params.get('panel') === 'measurements' ? 'measurements' : undefined

  const { payload, state, canDraw, reload } = usePropertyMaps({
    propertyId, canEdit, measuring,
  })

  const [open, setOpen] = useState<OneMapPayload | null>(null)
  const [imagery, setImagery] = useState<
    { attribution: string; capturedAt?: string } | 'asking' | 'unavailable'
  >('asking')
  const [busy, setBusy] = useState(false)
  const [reopened, setReopened] = useState(0)
  const [naming, setNaming] = useState(false)
  const [switching, setSwitching] = useState(false)

  /*
   * Which map is open. `?map=` wins, because on a phone it is how somebody got
   * here from the cards (§6.1) and it is a link they can send; otherwise it is
   * the last-opened map, which `usePropertyMaps` already worked out.
   */
  const asked = params.get('map')
  const openId = state.kind === 'maps'
    ? (asked && payload?.maps.some((map) => map.id === asked && !map.archived)
      ? asked
      : state.openId)
    : null

  /* The map this property was last looked at on, or the first one. */
  useEffect(() => {
    if (!openId) { setOpen(null); return }
    let live = true
    oneMap(openId)
      .then((body) => {
        if (!live) return
        setOpen(body)
        rememberLastMap(propertyId, openId)
      })
      .catch(() => { if (live) setOpen(null) })
    return () => { live = false }
  }, [openId, propertyId, reopened])

  /**
   * Re-read the open map.
   *
   * A layer added or deleted changes the whole set, which the editor's own
   * optimistic state cannot work out — so those two go through here. A shape
   * does not: `useMapEditing` already knows what it did, and re-fetching
   * after every stroke is a round trip the map does not need.
   */
  const reopen = useCallback(() => setReopened((n) => n + 1), [])

  /*
   * The Empty screen's footnote says whether the imagery is ready, so it has
   * to ask — and asking is free after the first time (`lib/map-provider.ts`
   * caches the session for its own lifetime).
   */
  useEffect(() => {
    if (state.kind !== 'empty') return
    let live = true
    imagerySession()
      .then((session) => {
        if (live) {
          setImagery({
            attribution: session.attribution,
            ...(session.capturedAt ? { capturedAt: session.capturedAt } : {}),
          })
        }
      })
      .catch(() => { if (live) setImagery('unavailable') })
    return () => { live = false }
  }, [state.kind])

  /*
   * The phone's green action (§6.1) arrives as a flag in the URL rather than
   * as a callback, because the button that sets it lives in the record page's
   * bottom bar — two components away — and a link is the one thing both ends
   * already agree on. Each is consumed once and then dropped, so a reload
   * does not re-fire it.
   */
  useEffect(() => {
    if (params.get('open') !== '1') return
    const next = new URLSearchParams(params)
    next.delete('open')
    if (openId) next.set('map', openId)
    setParams(next, { replace: true })
  }, [params, setParams, openId])

  const startMeasuring = useCallback(() => {
    const next = new URLSearchParams(params)
    next.set('measure', '1')
    setParams(next, { replace: true })
  }, [params, setParams])

  const stopMeasuring = useCallback(() => {
    const next = new URLSearchParams(params)
    next.delete('measure')
    setParams(next, { replace: true })
    /* The property's own measurements changed, so the record has to re-read
       them: Overview › About › Measured is where they land. */
    onPropertyChanged()
  }, [params, setParams, onPropertyChanged])

  const create = useCallback(async (body: {
    name: string; from: 'blank' | { copyMapId: string }; firstLayer: boolean
  }) => {
    setBusy(true)
    try {
      const made = await createMap(propertyId, body)
      rememberLastMap(propertyId, made.id)
      setNaming(false)
      const next = new URLSearchParams(params)
      next.set('map', made.id)
      setParams(next, { replace: true })
      reload()
    } finally {
      setBusy(false)
    }
  }, [propertyId, params, setParams, reload])

  const savePin = useCallback(async (at: { lng: number; lat: number }) => {
    /*
     * The ordinary property update, not a route of this feature's own (§4.4):
     * a property's coordinates have one writer and Navigate, the crew app and
     * this screen all read the same field.
     */
    await request(`/properties/${propertyId}`, {
      method: 'PATCH',
      body: { latitude: at.lat, longitude: at.lng, version: propertyVersion },
    })
    onPropertyChanged()
    reload()
  }, [propertyId, propertyVersion, onPropertyChanged, reload])

  useEffect(() => {
    if (params.get('new') !== '1') return
    const next = new URLSearchParams(params)
    next.delete('new')
    setParams(next, { replace: true })
    setNaming(true)
  }, [params, setParams])

  const shapesByLayer = useMemo(() => countByLayer(open), [open])

  const openOne = useCallback((mapId: string) => {
    rememberLastMap(propertyId, mapId)
    setSwitching(false)
    const next = new URLSearchParams(params)
    next.set('map', mapId)
    setParams(next, { replace: true })
  }, [propertyId, params, setParams])

  /*
   * The two dialogs the tab owns rather than the editor: both are about
   * *which* map, which is a question the editor cannot answer — it only ever
   * has one.
   */
  const dialogs = (
    <>
      {naming && (
        <NewMapDialog
          propertyName={propertyName}
          maps={payload?.maps.filter((one) => !one.archived) ?? []}
          onCancel={() => setNaming(false)}
          onCreate={create}
        />
      )}
      {switching && (
        <MapSwitcher
          maps={payload?.maps ?? []}
          openId={openId}
          canEdit={canDraw}
          offerMeasure={payload?.features.measurements ?? false}
          onOpen={openOne}
          onNew={() => { setSwitching(false); setNaming(true) }}
          onMeasure={() => { setSwitching(false); startMeasuring() }}
          onClose={() => setSwitching(false)}
        />
      )}
    </>
  )

  if (state.kind === 'loading') {
    return <p className="p-4 text-cell text-text-muted">Loading the maps…</p>
  }

  if (state.kind === 'error') {
    return (
      <EmptyState
        title="The maps could not be loaded"
        body={state.message}
        action={{ label: 'Try again', onClick: reload }}
      />
    )
  }

  if (state.kind === 'no_location') {
    return <NoMapLocation address={address} canEdit={canEdit} onSave={savePin} />
  }

  /*
   * A measure-only session is the editor with no map behind it (§5.4): the
   * same canvas, the same tools, the tape instead of layers. `EMPTY_MAP` is
   * what stands in for the map it does not have — a real one would be a map
   * nobody asked for, and the session's whole point is that nothing is saved
   * unless somebody says so.
   */
  if (state.kind === 'measure') {
    return (
      <>
        <MapEditor
          payload={open ?? emptyMap(propertyName)}
          pin={[payload!.location!.lng, payload!.location!.lat]}
          propertyId={propertyId}
          propertyName={propertyName}
          canDraw={canDraw}
          offerMeasure={payload?.features.measurements ?? false}
          measuring
          onReload={reopen}
          onDoneMeasuring={stopMeasuring}
        />
        {dialogs}
      </>
    )
  }

  /*
   * A phone's Map tab is cards, and the editor opens **over** them (§6.1):
   * the same `MapEditor`, which draws its own full-screen shell at this
   * width (§6.2) — fixed, `100dvh`, the Property chrome gone and the map
   * with the whole screen.
   */
  if (phone && asked && open) {
    return (
      <>
        <MapEditor
          payload={open}
          pin={[payload!.location!.lng, payload!.location!.lat]}
          propertyId={propertyId}
          propertyName={propertyName}
          canDraw={canDraw}
          offerMeasure={payload?.features.measurements ?? false}
          onReload={reopen}
          onSwitch={() => setSwitching(true)}
          onMeasure={startMeasuring}
          openPanel={panel}
          onBack={() => {
            const next = new URLSearchParams(params)
            next.delete('map')
            setParams(next, { replace: true })
          }}
        />
        {dialogs}
      </>
    )
  }

  if (phone) {
    return (
      <PhoneMapTab
        propertyName={propertyName}
        maps={payload?.maps ?? []}
        measurements={payload?.measurementsSummary ?? []}
        openId={openId}
        openLayers={open ? { layers: open.layers, shapes: shapesByLayer } : null}
        divisions={open?.divisions ?? []}
        canEdit={canDraw}
        offerMeasuring={payload?.features.measurements ?? false}
        onCreate={() => setNaming(true)}
        onMeasure={startMeasuring}
        onOpen={(mapId) => {
          rememberLastMap(propertyId, mapId)
          const next = new URLSearchParams(params)
          next.set('map', mapId)
          setParams(next, { replace: true })
        }}
        onSeeMeasurements={onOpenMeasurements}
      />
    )
  }

  if (state.kind === 'empty') {
    return (
      <>
        <MapEmptyScreen
          propertyName={propertyName}
          address={address}
          measurements={payload?.measurementsSummary ?? []}
          imagery={imagery}
          canEdit={canDraw}
          offerMeasuring={payload?.features.measurements ?? false}
          onCreate={() => setNaming(true)}
          onMeasure={startMeasuring}
          onSeeMeasurements={onOpenMeasurements}
        />
        {busy && (
          <p className="px-4 pb-4 text-cell text-text-muted" role="status">Making the map…</p>
        )}
        {dialogs}
      </>
    )
  }

  if (!open) return <p className="p-4 text-cell text-text-muted">Opening the map…</p>

  return (
    <>
      <MapEditor
        payload={open}
        pin={[payload!.location!.lng, payload!.location!.lat]}
        propertyId={propertyId}
        propertyName={propertyName}
        canDraw={canDraw}
        offerMeasure={payload?.features.measurements ?? false}
        onReload={reopen}
        onSwitch={() => setSwitching(true)}
        onMeasure={startMeasuring}
        openPanel={panel}
      />
      {dialogs}
    </>
  )
}

/**
 * The map a measure-only session does not have.
 *
 * A session draws on the canvas and saves nothing to a map, so the editor
 * still needs a `map` to render a camera and a name — but creating a real one
 * would leave a map on the property that nobody asked for, which is the one
 * thing §5.4 is careful about. Its id is empty, so nothing can be written to
 * it by accident: every map write takes an id and an empty one is a 404.
 */
function emptyMap(propertyName: string): OneMapPayload {
  return {
    map: {
      id: '', name: `${propertyName} · measuring`, base: 'satellite',
      archived: false, view: null, version: 1,
    },
    layers: [],
    features: [],
    divisions: [],
  } as unknown as OneMapPayload
}

/** How many shapes sit on each layer, for the phone card's rows. */
function countByLayer(payload: OneMapPayload | null): Record<string, number> {
  const counts: Record<string, number> = {}
  for (const layer of payload?.layers ?? []) counts[layer.id] = 0
  for (const feature of payload?.features ?? []) {
    counts[feature.layerId] = (counts[feature.layerId] ?? 0) + 1
  }
  return counts
}

/** Re-exported so the record page's quick action can name the open map. */
export type { MapLayer }
